import loginBg from '@/assets/icons/login-bg.png';
import { AlloSquareLogo } from '@/components/logos/allo-logo';
import { consumeOAuthConsentReturn } from '@/lib/oauth-consent-return';
import { $fetchAllo } from '@/services/api-allo/client';
import { supabase } from '@/services/supabase';
import * as Sentry from '@sentry/react';
import { Outlet, createFileRoute, redirect, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';

export const Route = createFileRoute('/login')({
  component: RouteComponent,
  beforeLoad: async () => {
    const authSess = await supabase.auth.getSession();

    if (authSess.data.session) {
      const [user] = await Promise.all([(await $fetchAllo.GET('/v1/user')).data?.data]);

      if (user) {
        const consentReturn = consumeOAuthConsentReturn();
        if (consentReturn) {
          throw redirect({ href: consentReturn, replace: true });
        }
        throw redirect({ to: '/', replace: true });
      }
    }

    /**
     * @see Analytics-Sentry
     */
    Sentry.setUser(null);
  },
});

function RouteComponent() {
  const { update } = useIntercom();
  const { pathname } = useLocation();
  const [hasBeenOnOtpScreen, setHasBeenOnOtpScreen] = useState(false);

  // Check if we're currently on the OTP screen
  const isCurrentlyOnOtpScreen =
    pathname === '/login/otp-code' || pathname.startsWith('/login/otp-code');

  // Track if user has been on OTP screen and maintain 600px width
  const shouldUseWideContainer = isCurrentlyOnOtpScreen || hasBeenOnOtpScreen;

  // Update state when user enters OTP screen
  useEffect(() => {
    if (isCurrentlyOnOtpScreen) {
      setHasBeenOnOtpScreen(true);
    }
  }, [isCurrentlyOnOtpScreen]);

  // Reset state when user leaves login flow entirely
  useEffect(() => {
    if (pathname === '/login' && hasBeenOnOtpScreen) {
      setHasBeenOnOtpScreen(false);
    }
  }, [pathname, hasBeenOnOtpScreen]);

  /**
   * @see Analytics-Intercom
   */
  useEffect(() => {
    update({
      userId: undefined,
    });
  }, [update]);

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url("${loginBg}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="flex justify-center items-center min-h-screen">
        <div
          className={`${shouldUseWideContainer ? 'w-[600px]' : 'w-[400px]'} bg-white rounded-2xl p-8 pt-10 shadow-sm`}
        >
          <div className="flex flex-col items-center gap-6">
            <AlloSquareLogo />
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
